import { useGT } from 'gt-react';
import { skipToken, useMutation, useQuery } from '@tanstack/react-query';
import { NO_SOP_STEPS_DETECTED_MESSAGE } from '@erp/rpc';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type {
  AcceptSopImportInput,
  AddSopRefStepInput,
  AddActionStepInput,
  AgentSopDto,
  UpdateSopStepInput,
  ApplySopEditInput,
  CreateSopDraftInput,
  CreateSopDraftResponse,
  DeleteSopDraftInput,
  FinalizeSopVersionInput,
  FinalizeSopVersionResponse,
  PublishSopVersionInput,
  PublishSopVersionResponse,
  RegenerateSopChangelogInput,
  UpdateSopVersionChangelogInput,
  FinalizeInterviewInput,
  FinalizeInterviewResponse,
  ImportSopInput,
  ImportSopBundleDraftInput,
  ImportSopBundleDraftResponse,
  ImportSopBundleInput,
  ImportSopBundleResponse,
  StartSopImportInput,
  InstallBuiltinSopInput,
  InstallBuiltinSopResponse,
  InterruptInterviewInput,
  ListSopsInput,
  PromoteLiteralToParamInput,
  RemoveStepInput,
  ReorderStepsInput,
  RespondToInterviewInput,
  SetInterviewPhaseInput,
  StartInterviewInput,
  StartSopRunInput,
  SubmitInterviewAnnotationInput,
  SubmitInterviewBranchInput,
  SubmitInterviewCaptureInput,
  SubmitInterviewCredentialsInput,
  SubmitInterviewTakeoverInput,
  UpdateInterviewParametersInput,
  UpdateSopParametersInput,
  UpdateSopSessionRulesInput,
  UpdateSopOutputsInput,
  UpdateSopLoginConfigInput,
  RenameSopInput,
  DeleteSopInput,
} from '@erp/rpc';

/**
 * RPC hooks for the bidirectional interview → SOP authoring flow (AI-166).
 *
 * The start* procedures return a `{ sessionId }` that the caller feeds into the
 * shared operator stream (`useOperatorSessionStream`) — the same AG-UI
 * subscription used for live agent runs. Interview confirms ride
 * `respondToInterview`; persisting the draft rides `finalizeInterview`.
 *
 * NOTE: startInterview / respondToInterview / finalizeInterview / startSopRun are
 * wired to the background session runner, but the live A2A resume + Kernel browser
 * path is unverified until the manual E2E (AI-166 #10).
 */

// --- Interview authoring mutations ---

export function useStartInterview(options?: {
  onSuccess?: (data: { sessionId: string; agentRunId: string }) => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: StartInterviewInput) =>
      rpcClient.agent.startInterview(input),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start interview',
        color: 'error',
      });
    },
  });
}

export function useRespondToInterview(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: RespondToInterviewInput) =>
      rpcClient.agent.respondToInterview(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to send response',
        color: 'error',
      });
    },
  });
}

export function useInterruptInterview(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: InterruptInterviewInput) =>
      rpcClient.agent.interruptInterview(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to interrupt',
        color: 'error',
      });
    },
  });
}

export function useSubmitInterviewAnnotation(options?: {
  onSuccess?: () => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: SubmitInterviewAnnotationInput) =>
      rpcClient.agent.submitInterviewAnnotation(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to send annotation',
        color: 'error',
      });
    },
  });
}

export function useSubmitInterviewTakeover(options?: {
  onSuccess?: () => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: SubmitInterviewTakeoverInput) =>
      rpcClient.agent.submitInterviewTakeover(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to hand control back',
        color: 'error',
      });
    },
  });
}

/**
 * "Capture now" in a free-roam capture session (P1): drains the in-page gestures the human made
 * while walking the real site + a server-side screenshot, so the agent appends/refines one linear
 * SOP step (one trajectory frame). Carries NO payload — like takeover, the gestures live
 * server-side in the browser page. Mirrors `useSubmitInterviewTakeover`.
 */
export function useSubmitInterviewCapture(options?: {
  onSuccess?: () => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: SubmitInterviewCaptureInput) =>
      rpcClient.agent.submitInterviewCapture(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to capture the screen',
        color: 'error',
      });
    },
  });
}

/**
 * "Start an alternate path" in a free-roam capture session (P3): forks the trajectory under a
 * stated condition so the captures that follow attribute to the new path. Carries the condition
 * label + an optional 1-based divergence frame index; no screenshot/gestures.
 */
export function useSubmitInterviewBranch(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: SubmitInterviewBranchInput) =>
      rpcClient.agent.submitInterviewBranch(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start an alternate path',
        color: 'error',
      });
    },
  });
}

/**
 * Declare which lifecycle phase (setup / main / teardown) the interview is now teaching. Steps
 * captured afterward attribute to it; at finalize the steps stay in one SOP, tagged by phase and
 * ordered setup → main → teardown. Fire-and-forget from the UI — the active phase is reflected
 * optimistically in component state.
 */
export function useSetInterviewPhase(options?: { onSuccess?: () => void }) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: SetInterviewPhaseInput) =>
      rpcClient.agent.setInterviewPhase(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to switch the teaching phase',
        color: 'error',
      });
    },
  });
}

export function useSubmitInterviewCredentials(options?: {
  onSuccess?: () => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: SubmitInterviewCredentialsInput) =>
      rpcClient.agent.submitInterviewCredentials(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to send credentials',
        color: 'error',
      });
    },
  });
}

/**
 * Replace the draft's run-time parameters during a free-roam capture (human add/edit/remove).
 * Sends the full desired list; the interview runner stores it and rides it on the next
 * capture/branch/finalize directive, surfacing it live as `sopDraft.parameters`.
 */
export function useUpdateInterviewParameters(options?: {
  onSuccess?: () => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: UpdateInterviewParametersInput) =>
      rpcClient.agent.updateInterviewParameters(input),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update parameters',
        color: 'error',
      });
    },
  });
}

export function useFinalizeInterview(options?: {
  onSuccess?: (data: FinalizeInterviewResponse) => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.agent.listSops.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: FinalizeInterviewInput) =>
      rpcClient.agent.finalizeInterview(input),
    onSuccess: (data) => {
      showToast({
        title: 'Procedure saved',
        message: 'The procedure was saved successfully',
        color: 'success',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to finalize procedure',
        color: 'error',
      });
    },
  });
}

/** True when an import failed only because no step structure was recognized — the caller can
 * re-send with `allowUnstructured` after confirming with the author. */
export function isNoStepsDetectedError(error: Error | null): boolean {
  return error?.message === NO_SOP_STEPS_DETECTED_MESSAGE;
}

/** Import an existing markdown SOP as a new procedure. Returns the created SOP (navigate to it). */
export function useImportSop(options?: {
  onSuccess?: (sop: AgentSopDto) => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.agent.listSops.key(),
  ]);
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: ImportSopInput) => rpcClient.agent.importSop(input),
    onSuccess: (data) => {
      showToast({
        title: 'Procedure imported',
        message: 'A procedure was created from your markdown.',
        color: 'success',
      });
      invalidateRpcQueryFn({});
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      // "No steps detected" is a prompt, not a failure — the dialog asks the author whether to
      // import the document anyway, so a red toast would only be noise.
      if (isNoStepsDetectedError(error)) return;
      showToast({
        title: 'Error',
        message: error.message || 'Failed to import procedure',
        color: 'error',
      });
    },
  });
}

/** Begin a durable, review-first import. The caller navigates to the returned import resource. */
/** Create a new procedure from an exported `.sop.json` bundle. Success/error toasts are the
 * caller's — the dialog shows dropped-reference warnings inline and then navigates. */
export function useImportSopBundle(options?: {
  onSuccess?: (result: ImportSopBundleResponse) => void;
}) {
  const invalidateRpcQueryFn = useInvalidateRpcQueryFn((o) => [
    o.agent.listSops.key(),
  ]);

  return useMutation({
    mutationFn: (input: ImportSopBundleInput) =>
      rpcClient.agent.importSopBundle(input),
    onSuccess: (result) => {
      invalidateRpcQueryFn({});
      options?.onSuccess?.(result);
    },
  });
}

export function useStartSopImport(options?: {
  onSuccess?: (result: { importId: string; backgroundJobId: string }) => void;
}) {
  return useMutation({
    mutationFn: (input: StartSopImportInput) =>
      rpcClient.agent.startSopImport(input),
    onSuccess: (result) => options?.onSuccess?.(result),
  });
}

export function useSopImport(
  organizationId: string | undefined,
  importId: string | undefined,
) {
  return useQuery({
    ...orpc.agent.getSopImport.queryOptions({
      input:
        organizationId && importId ? { organizationId, importId } : skipToken,
    }),
    enabled: Boolean(organizationId && importId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'processing' ? 2_000 : false;
    },
  });
}

/** Refresh the cached import after a mutation so `useSopImport` sees the new status (and, for a
 * retry, resumes polling); without it the review page sits on "Failed" until a reload. */
function useInvalidateSopImport() {
  return useInvalidateRpcQueryFn((o) => [o.agent.getSopImport.key()]);
}

export function useRetrySopImport() {
  const invalidate = useInvalidateSopImport();
  return useMutation({
    mutationFn: (input: { organizationId: string; importId: string }) =>
      rpcClient.agent.retrySopImport(input),
    onSuccess: () => {
      invalidate({});
    },
  });
}

export function useDiscardSopImport(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopImport();
  return useMutation({
    mutationFn: (input: { organizationId: string; importId: string }) =>
      rpcClient.agent.discardSopImport(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
  });
}

export function useAcceptSopImport(options?: {
  onSuccess?: (sop: AgentSopDto) => void;
}) {
  const invalidate = useInvalidateRpcQueryFn((o) => [
    o.agent.listSops.key(),
    o.agent.getSop.key(),
  ]);
  return useMutation({
    mutationFn: (input: AcceptSopImportInput) =>
      rpcClient.agent.acceptSopImport(input),
    onSuccess: (sop) => {
      invalidate({});
      options?.onSuccess?.(sop);
    },
  });
}

// --- SOP replay mutation ---

export function useStartSopRun(options?: {
  onSuccess?: (result: { sessionId: string; agentRunId: string }) => void;
}) {
  const { showToast } = useToast();

  return useMutation({
    // ponytail: runner is wired; the live A2A/Kernel path is unverified until the #10 E2E.
    mutationFn: (input: StartSopRunInput) => rpcClient.agent.startSopRun(input),
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to start procedure run',
        color: 'error',
      });
    },
  });
}

// --- SOP composition + parameter editing (AI-246) ---
// Each edit returns the SOP's new current version, so we invalidate the detail + list reads.

function useInvalidateSopReads() {
  return useInvalidateRpcQueryFn((o) => [
    o.agent.getSop.key(),
    o.agent.listSops.key(),
    // The built-in catalog reports per-org install state (installed / update available), so any
    // write that supersedes a SOP can change it too.
    o.agent.listBuiltinSops.key(),
  ]);
}

export function useAddSopRefStep(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: AddSopRefStepInput) =>
      rpcClient.agent.addSopRefStep(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to embed procedure',
        color: 'error',
      });
    },
  });
}

export function useAddActionStep(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: AddActionStepInput) =>
      rpcClient.agent.addActionStep(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to add step',
        color: 'error',
      });
    },
  });
}

export function useRemoveStep(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: RemoveStepInput) => rpcClient.agent.removeStep(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to remove step',
        color: 'error',
      });
    },
  });
}

/**
 * Apply a whole-procedure edit — the accept half of an AI edit proposal.
 *
 * Unlike the other step mutations this can fail because the procedure moved on underneath the
 * edit (a replay self-heal, another editor): the server answers 409 rather than merging blindly.
 * The error is handed to the caller instead of toasted so the proposal card can say what happened
 * and offer the only useful next step — discard and ask again.
 */
export function useApplySopEdit(options?: {
  onSuccess?: (sop: AgentSopDto) => void;
  onError?: (error: Error) => void;
}) {
  const invalidate = useInvalidateSopReads();
  return useMutation({
    mutationFn: (input: ApplySopEditInput) =>
      rpcClient.agent.applySopEdit(input),
    onSuccess: (sop) => {
      invalidate({});
      options?.onSuccess?.(sop);
    },
    onError: (error: Error) => options?.onError?.(error),
  });
}

export function useReorderSteps(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: ReorderStepsInput) =>
      rpcClient.agent.reorderSteps(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to reorder steps',
        color: 'error',
      });
    },
  });
}

export function useUpdateSopSessionRules(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  const gt = useGT();
  return useMutation({
    mutationFn: (input: UpdateSopSessionRulesInput) =>
      rpcClient.agent.updateSopSessionRules(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) =>
      showToast({
        title: gt('Could Not Save Session Rules', {
          context: 'procedure session rules save error',
        }),
        message: error.message,
        color: 'error',
      }),
  });
}

export function useUpdateSopOutputs(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  const gt = useGT();
  return useMutation({
    mutationFn: (input: UpdateSopOutputsInput) =>
      rpcClient.agent.updateSopOutputs(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) =>
      showToast({
        title: gt('Could Not Save Outputs', {
          context: 'procedure outputs save error',
        }),
        message: error.message,
        color: 'error',
      }),
  });
}

export function useUpdateSopParameters(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: UpdateSopParametersInput) =>
      rpcClient.agent.updateSopParameters(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update parameters',
        color: 'error',
      });
    },
  });
}

export function useRenameSop() {
  const invalidate = useInvalidateSopReads();
  return useMutation({
    mutationFn: (input: RenameSopInput) => rpcClient.agent.renameSop(input),
    onSuccess: () => {
      invalidate({});
    },
  });
}

export function useDeleteSop() {
  const invalidate = useInvalidateSopReads();
  return useMutation({
    mutationFn: (input: DeleteSopInput) => rpcClient.agent.deleteSop(input),
    onSuccess: () => {
      invalidate({});
    },
  });
}

export function useUpdateSopLoginConfig(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: UpdateSopLoginConfigInput) =>
      rpcClient.agent.updateSopLoginConfig(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update login config',
        color: 'error',
      });
    },
  });
}

// --- Built-in procedure catalog ---

/** The product's shipped procedures + this org's install state for each. */
export function useBuiltinSops(organizationId: string | undefined) {
  const query = useQuery({
    ...orpc.agent.listBuiltinSops.queryOptions({
      input: organizationId ? { organizationId } : skipToken,
    }),
    enabled: Boolean(organizationId),
  });
  return { ...query, builtins: query.data?.builtins ?? [] };
}

/** Prefill for a built-in's setup dialog, derived from an existing install or a legacy
 * remote-desktop agent definition. References only — never secrets. */
export function useBuiltinSopSetup(
  organizationId: string | undefined,
  builtinKey: string | undefined,
) {
  return useQuery({
    ...orpc.agent.getBuiltinSopSetup.queryOptions({
      input:
        organizationId && builtinKey
          ? { organizationId, builtinKey }
          : skipToken,
    }),
    enabled: Boolean(organizationId && builtinKey),
  });
}

export function useInstallBuiltinSop(options?: {
  onSuccess?: (response: InstallBuiltinSopResponse) => void;
}) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: InstallBuiltinSopInput) =>
      rpcClient.agent.installBuiltinSop(input),
    onSuccess: (response) => {
      invalidate({});
      options?.onSuccess?.(response);
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to install built-in procedure',
        color: 'error',
      });
    },
  });
}

export function usePromoteLiteralToParam(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopReads();
  const { showToast } = useToast();
  return useMutation({
    mutationFn: (input: PromoteLiteralToParamInput) =>
      rpcClient.agent.promoteLiteralToParam(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to create parameter',
        color: 'error',
      });
    },
  });
}

// --- SOP reads ---

export interface UseSopsOptions {
  organizationId: string | undefined;
  status?: ListSopsInput['status'];
  limit?: number;
  offset?: number;
}

export function useSops({
  organizationId,
  status,
  limit = 50,
  offset = 0,
}: UseSopsOptions) {
  const query = useQuery({
    ...orpc.agent.listSops.queryOptions({
      input: organizationId
        ? { organizationId, status, limit, offset }
        : skipToken,
    }),
    enabled: Boolean(organizationId),
  });

  return { ...query, sops: query.data?.sops ?? [] };
}

export function useSop(
  organizationId: string | undefined,
  sopId: string | undefined,
  /** Read a specific version (`/v/$versionNumber`) instead of the published one (AI-461). */
  version?: number,
) {
  return useQuery({
    ...orpc.agent.getSop.queryOptions({
      input:
        organizationId && sopId
          ? { organizationId, sopId, version }
          : skipToken,
    }),
    enabled: Boolean(organizationId && sopId),
  });
}

// --- Version history (AI-461) ---

export function useSopVersions(
  organizationId: string | undefined,
  sopId: string | undefined,
) {
  return useQuery({
    ...orpc.agent.listSopVersions.queryOptions({
      input: organizationId && sopId ? { organizationId, sopId } : skipToken,
    }),
    enabled: Boolean(organizationId && sopId),
  });
}

function useInvalidateSopVersionReads() {
  return useInvalidateRpcQueryFn((o) => [
    o.agent.getSop.key(),
    o.agent.listSops.key(),
    o.agent.listSopVersions.key(),
  ]);
}

/** Fork a personal draft (or resume the caller's open one). Errors surface to the caller — the
 * page routes to the draft on success, so a toast alone would strand the user. */
export function useCreateSopDraft(options?: {
  onSuccess?: (result: CreateSopDraftResponse) => void;
}) {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: CreateSopDraftInput) =>
      rpcClient.agent.createSopDraft(input),
    onSuccess: (result) => {
      invalidate({});
      options?.onSuccess?.(result);
    },
  });
}

/** Write an exported `.sop.json` bundle as the caller's new draft of this procedure. Errors
 * surface to the caller — the dialog shows them inline (an open draft refuses the import). */
export function useImportSopBundleDraft(options?: {
  onSuccess?: (result: ImportSopBundleDraftResponse) => void;
}) {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: ImportSopBundleDraftInput) =>
      rpcClient.agent.importSopBundleDraft(input),
    onSuccess: (result) => {
      invalidate({});
      options?.onSuccess?.(result);
    },
  });
}

/** Freeze + (by default) publish a draft. A `{ok:false}` result is the stale-base warning, not an
 * error — the caller re-asks with `acknowledgeStaleBase`. */
export function useFinalizeSopVersion(options?: {
  onSuccess?: (result: FinalizeSopVersionResponse) => void;
}) {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: FinalizeSopVersionInput) =>
      rpcClient.agent.finalizeSopVersion(input),
    onSuccess: (result) => {
      if (result.ok) invalidate({});
      options?.onSuccess?.(result);
    },
  });
}

export function usePublishSopVersion(options?: {
  onSuccess?: (result: PublishSopVersionResponse) => void;
}) {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: PublishSopVersionInput) =>
      rpcClient.agent.publishSopVersion(input),
    onSuccess: (result) => {
      if (result.ok) invalidate({});
      options?.onSuccess?.(result);
    },
  });
}

export function useDeleteSopDraft(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: DeleteSopDraftInput) =>
      rpcClient.agent.deleteSopDraft(input),
    onSuccess: () => {
      invalidate({});
      options?.onSuccess?.();
    },
  });
}

export function useUpdateSopVersionChangelog() {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: UpdateSopVersionChangelogInput) =>
      rpcClient.agent.updateSopVersionChangelog(input),
    onSuccess: () => invalidate({}),
  });
}

export function useRegenerateSopChangelog() {
  const invalidate = useInvalidateSopVersionReads();
  return useMutation({
    mutationFn: (input: RegenerateSopChangelogInput) =>
      rpcClient.agent.regenerateSopChangelog(input),
    onSuccess: () => invalidate({}),
  });
}

/** Golden-capture status for a mock-backed SOP (AI-340 U3): has/stale + the last replay's heals, so
 * the SOP page can show status, confirm a fresh capture, and explain a heal-blocked one. */
export function useSopGoldenStatus(
  organizationId: string | undefined,
  sopId: string | undefined,
) {
  return useQuery({
    ...orpc.agent.getSopGoldenStatus.queryOptions({
      input: organizationId && sopId ? { organizationId, sopId } : skipToken,
    }),
    enabled: Boolean(organizationId && sopId),
  });
}

export function useUpdateSopStep() {
  const invalidate = useInvalidateSopReads();
  return useMutation({
    mutationFn: (input: UpdateSopStepInput) =>
      rpcClient.agent.updateSopStep(input),
    onSuccess: () => invalidate({}),
    onError: () => invalidate({}),
  });
}
